import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchBettingItem, initiateBet, fetchBetResult } from '../api/api';
import BoxSelector from '../components/BoxSelector';
import ConfirmationModal from '../components/ConfirmationModal';
import PaymentModal from '../components/PaymentModal';
import RevealSequence from '../components/RevealSequence';
import LoadingSpinner from '../components/LoadingSpinner';
import './BoxSelectionPage.css';

const BoxSelectionPage = () => {
  const { itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [item, setItem] = useState(location.state?.item || null);
  const [loading, setLoading] = useState(!item);
  const [selectedBox, setSelectedBox] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [betResult, setBetResult] = useState(null);
  const [showReveal, setShowReveal] = useState(false);

  useEffect(() => {
    if (!item) {
      loadItem();
    }
  }, [itemId]);

  const loadItem = async () => {
    try {
      const data = await fetchBettingItem(itemId);
      if (data) {
        setItem(data);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading item:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleBoxSelect = (boxNumber) => {
    setSelectedBox(boxNumber);
  };

  const handleProceed = () => {
    if (selectedBox) {
      setShowConfirmation(true);
    }
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    setShowPayment(true);
  };

  const handlePaymentSubmit = async (phoneNumber) => {
    setIsProcessing(true);
    
    try {
      // Initiate the bet
      const response = await initiateBet(item.id, selectedBox, phoneNumber);
      
      if (response.success) {
        // Fetch the result
        const result = await fetchBetResult(response.betId);
        setBetResult(result);
        setShowPayment(false);
        setShowReveal(true);
      }
    } catch (error) {
      console.error('Error processing bet:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRevealComplete = () => {
    setShowReveal(false);
    setBetResult(null);
    setSelectedBox(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="box-selection-page loading-state">
        <LoadingSpinner text="Loading game..." size="large" />
      </div>
    );
  }

  if (!item) return null;

  return (
    <div className="box-selection-page">
      {/* Background Effects */}
      <div className="page-bg">
        <div className="bg-gradient" />
        <motion.div 
          className="bg-pulse"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </div>

      {/* Page Content */}
      <motion.div 
        className="page-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="game-header">
          <motion.button
            className="back-button"
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            ← Back
          </motion.button>
          
          <motion.div 
            className="game-info"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="game-icon">{item.image}</span>
            <div className="game-details">
              <h1 className="game-title">{item.name}</h1>
              <p className="game-subtitle">{item.description}</p>
            </div>
          </motion.div>

          <div className="game-cost">
            <span className="cost-label">Entry Fee</span>
            <span className="cost-amount">KES {item.cost}</span>
          </div>
        </div>

        {/* Instructions */}
        <motion.div 
          className="instructions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="instructions-title">
            <span className="instruction-icon">👆</span>
            Select Your Lucky Box
          </h2>
          <p className="instructions-text">
            Each box contains a different prize. Trust your instincts and pick wisely!
          </p>
        </motion.div>

        {/* Box Selector */}
        <motion.div 
          className="selector-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <BoxSelector
            onSelect={handleBoxSelect}
            selectedBox={selectedBox}
            disabled={showConfirmation || showPayment || isProcessing}
          />
        </motion.div>

        {/* Prize Info */}
        <motion.div 
          className="prize-info"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="prize-range">
            <span className="range-label">Prizes Range</span>
            <div className="range-values">
              <span className="range-min">{item.minPrize}</span>
              <span className="range-arrow">→</span>
              <span className="range-max">{item.maxPrize}</span>
            </div>
          </div>
        </motion.div>

        {/* Proceed Button */}
        <AnimatePresence>
          {selectedBox && (
            <motion.div 
              className="action-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <motion.button
                className="proceed-button"
                onClick={handleProceed}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Proceed with Box {selectedBox}</span>
                <span className="btn-arrow">→</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirm}
        item={item}
        boxNumber={selectedBox}
      />

      <PaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onSubmit={handlePaymentSubmit}
        item={item}
        isProcessing={isProcessing}
      />

      {/* Reveal Sequence */}
      <AnimatePresence>
        {showReveal && betResult && (
          <RevealSequence
            result={betResult}
            onComplete={handleRevealComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BoxSelectionPage;

